/**
 * Drag to zoom (TRA-1437), mouse only: press, drag sideways, release. The
 * touch handlers stay unwired, so a finger still scrolls the page. Leaving
 * the chart mid-drag drops the drag. Shared by the replicas chart and every
 * chart on the metrics page (TRA-1592); `range` says what a drag between
 * two chart times means, the plain stretch unless the chart's buckets say
 * otherwise.
 */

import { useState } from 'react'
import { ReferenceArea } from 'recharts'

import { dragRange, type TimeRange } from '@/components/models/home/replicaZoom'

/** The stretch of a drag so far, in chart time, in the order it was dragged. */
export interface Drag {
  from: number
  to: number
}

/** What recharts hands a chart mouse handler: the x value under the pointer. */
export interface ChartPointer {
  activeLabel: string | number | undefined
}

export interface DragHandlers {
  onMouseDown: (pointer: ChartPointer) => void
  onMouseMove: (pointer: ChartPointer) => void
  onMouseUp: () => void
  onMouseLeave: () => void
}

function epochAt(pointer: ChartPointer): number | null {
  return typeof pointer.activeLabel === 'number' ? pointer.activeLabel : null
}

export function useDragZoom(
  onZoom: (zoom: TimeRange) => void,
  range: (from: number, to: number) => TimeRange | null = dragRange,
): { drag: Drag | null; handlers: DragHandlers } {
  const [drag, setDrag] = useState<Drag | null>(null)
  return {
    drag,
    handlers: {
      onMouseDown: (pointer) => {
        const epoch = epochAt(pointer)
        if (epoch !== null) setDrag({ from: epoch, to: epoch })
      },
      onMouseMove: (pointer) => {
        const epoch = epochAt(pointer)
        if (epoch !== null) setDrag((d) => (d === null ? null : { ...d, to: epoch }))
      },
      onMouseUp: () => {
        setDrag(null)
        if (drag === null) return
        const zoom = range(drag.from, drag.to)
        if (zoom !== null) onZoom(zoom)
      },
      onMouseLeave: () => { setDrag(null) },
    },
  }
}

/** The shaded stretch of a drag in progress, placed inside the chart; nothing between drags. */
export function dragShade(drag: Drag | null): React.JSX.Element | null {
  if (drag === null) return null
  return (
    <ReferenceArea
      x1={Math.min(drag.from, drag.to)}
      x2={Math.max(drag.from, drag.to)}
      fill="hsl(var(--muted-foreground))"
      fillOpacity={0.15}
      stroke="none"
    />
  )
}
